import { Value } from '@sinclair/typebox/value'
import { isString } from '@repo/shared/utils/type-guard.util'
import { StreamerWorkspaceBootstrapResponseSchema, type StreamerWorkspaceBootstrapResponse } from '@repo/shared/dto'

import { streamerApi } from '@client-streamer/libs/eden'

export type StreamerFoundationFailureCode = 'realm_join_failed' | 'workspace_bootstrap_failed'

export class StreamerFoundationBootstrapError extends Error {
  constructor(readonly code: StreamerFoundationFailureCode) {
    super(code)
    this.name = 'StreamerFoundationBootstrapError'
  }
}

export interface StreamerFoundationTransport {
  joinRealm(): Promise<{ error: unknown }>
  bootstrapWorkspace(): Promise<{
    data: StreamerWorkspaceBootstrapResponse | null
    error: unknown
  }>
}

const defaultTransport: StreamerFoundationTransport = {
  async joinRealm() {
    return await streamerApi.v2.realm.join.post()
  },
  async bootstrapWorkspace() {
    return await streamerApi.v2.workspaces.bootstrap.post()
  },
}

let completedBootstrap: {
  userId: string
  response: StreamerWorkspaceBootstrapResponse
} | null = null
let activeBootstrap: {
  userId: string
  promise: Promise<StreamerWorkspaceBootstrapResponse>
} | null = null
let bootstrapEpoch = 0

export function hasCompletedStreamerFoundation(userId: string): boolean {
  return completedBootstrap?.userId === userId
}

export function resetStreamerFoundationForTests(): void {
  completedBootstrap = null
  activeBootstrap = null
  bootstrapEpoch += 1
}

/**
 * Explicitly joins the product realm before creating or loading the user's
 * personal workspace. Both operations are idempotent; the in-memory fence only
 * prevents duplicate requests during one dashboard lifecycle.
 */
export async function ensureStreamerFoundation(
  userId: string,
  transport: StreamerFoundationTransport = defaultTransport,
): Promise<StreamerWorkspaceBootstrapResponse> {
  const normalizedUserId = userId.trim()
  if (!normalizedUserId) {
    throw new StreamerFoundationBootstrapError('realm_join_failed')
  }

  if (completedBootstrap?.userId === normalizedUserId) {
    return completedBootstrap.response
  }
  if (completedBootstrap && completedBootstrap.userId !== normalizedUserId) {
    completedBootstrap = null
    bootstrapEpoch += 1
  }
  if (activeBootstrap?.userId === normalizedUserId) {
    return await activeBootstrap.promise
  }

  const requestEpoch = ++bootstrapEpoch
  const promise = (async (): Promise<StreamerWorkspaceBootstrapResponse> => {
    const realmResult = await transport.joinRealm()
    if (realmResult.error) {
      throw new StreamerFoundationBootstrapError('realm_join_failed')
    }

    const workspaceResult = await transport.bootstrapWorkspace()
    if (workspaceResult.error || !workspaceResult.data || !Value.Check(StreamerWorkspaceBootstrapResponseSchema, workspaceResult.data)) {
      throw new StreamerFoundationBootstrapError('workspace_bootstrap_failed')
    }

    if (requestEpoch === bootstrapEpoch) {
      completedBootstrap = {
        userId: normalizedUserId,
        response: workspaceResult.data,
      }
    }
    return workspaceResult.data
  })()

  activeBootstrap = {
    userId: normalizedUserId,
    promise,
  }

  try {
    return await promise
  } finally {
    if (activeBootstrap?.promise === promise) activeBootstrap = null
  }
}

export function resolveFoundationRedirect<T>(value: T): string {
  if (!isString(value) || !value.startsWith('/') || value.startsWith('//') || value === '/setup' || value.startsWith('/setup?')) {
    return '/'
  }

  return value
}
